/**
 * MetricCard Component - Reusable metric display card
 * Consistent with billing app design patterns but optimized for admin analytics
 */

import React from 'react';
import {
  Card,
  Box,
  Text,
  InlineStack,
  BlockStack,
  Spinner,
  Badge,
  Tooltip,
  Icon,
} from '@shopify/polaris';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  InfoIcon,
} from '@shopify/polaris-icons';
import apiClient from '../../utils/api';

export default function MetricCard({
  title,
  value,
  change,
  icon: IconComponent,
  color = 'default',
  loading = false,
  error = null,
  tooltip,
  subtitle,
  badge,
  className = '',
  formatter,
  ...props
}) {
  // Validate props to prevent crashes
  if (!title || (typeof title !== 'string' && typeof title !== 'number')) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('MetricCard: Invalid title prop provided:', title);
    }
    return null;
  }
  // Loading state with skeleton pattern matching billing app
  if (loading) {
    return (
      <Card {...props}>
        <Box padding="400" height="200" style={{ display: 'flex', flexDirection: 'column' }}>
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              {IconComponent && (
                <Icon source={IconComponent} />
              )}
            </InlineStack>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text variant="bodySm" tone="subdued">Loading...</Text>
              </InlineStack>
              {subtitle && (
                <Box padding="100" background="surface-neutral-subdued" borderRadius="200" />
              )}
            </div>

            <div style={{ height: '32px' }}>
              {/* Reserved space for change indicator */}
            </div>
        </Box>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card {...props}>
        <Box padding="400" height="200" style={{ display: 'flex', flexDirection: 'column' }}>
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              <Tooltip content="Unable to load data">
                <Icon source={InfoIcon} tone="critical" />
              </Tooltip>
            </InlineStack>

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Text variant="headingLg" tone="critical">
                --
              </Text>
              <Text variant="bodySm" tone="critical">
                Error loading data
              </Text>
            </div>

            <div style={{ height: '32px' }}>
              {/* Reserved space for change indicator */}
            </div>
        </Box>
      </Card>
    );
  }

  // Format value based on type with safety checks
  const formatValue = (val) => {
    if (typeof val === 'number' && !isNaN(val) && isFinite(val)) {
      try {
        // If it looks like currency (large numbers), format as currency
        if (val > 100 || val < -100) {
          return apiClient.formatCurrency(val);
        }
        // Otherwise format as number with commas
        return new Intl.NumberFormat('en-US').format(val);
      } catch (error) {
        return '0';
      }
    }
    // Handle invalid numbers
    if (typeof val === 'number' && (isNaN(val) || !isFinite(val))) {
      return '--';
    }
    return val || '--';
  };

  // Determine color theme
  const getColorTheme = () => {
    switch (color) {
      case 'success':
        return {
          cardTone: 'success-subdued',
          valueTone: 'success',
          badgeTone: 'success',
        };
      case 'warning':
        return {
          cardTone: 'warning-subdued',
          valueTone: 'warning',
          badgeTone: 'warning',
        };
      case 'critical':
        return {
          cardTone: 'critical-subdued',
          valueTone: 'critical',
          badgeTone: 'critical',
        };
      default:
        return {
          cardTone: null,
          valueTone: 'base',
          badgeTone: 'info',
        };
    }
  };

  const colorTheme = getColorTheme();

  // Render change indicator (matching billing app pattern)
  const renderChange = () => {
    if (!change) return null;

    const isPositive = change.direction === 'up';
    const isNeutral = change.percentage === 0 || change.direction === 'neutral';
    
    if (isNeutral) {
      return (
        <Text variant="bodySm" tone="subdued">
          No change {change.period && `from ${change.period}`}
        </Text>
      );
    }

    return (
      <InlineStack gap="100" blockAlign="center">
        <Icon
          source={isPositive ? ArrowUpIcon : ArrowDownIcon}
          tone={isPositive ? 'success' : 'critical'}
        />
        <Text
          variant="bodySm"
          tone={isPositive ? 'success' : 'critical'}
          fontWeight="medium"
        >
          {(() => {
            const percentage = change.percentage;
            if (typeof percentage === 'number' && !isNaN(percentage) && isFinite(percentage)) {
              return Math.abs(percentage).toFixed(1) + '%';
            }
            return '0%';
          })()}
        </Text>
        {change.period && (
          <Text variant="bodySm" tone="subdued">
            {change.period}
          </Text>
        )}
        {change.isNew && (
          <Badge tone="info" size="small">
            New
          </Badge>
        )}
      </InlineStack>
    );
  };

  return (
    <Card {...props} background={colorTheme.cardTone} style={{ flex: 1 }}>
      <Box padding="400" height="200" style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header with title, tooltip, and icon */}
          <InlineStack align="space-between" blockAlign="center">
            <InlineStack gap="100" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                {title}
              </Text>
              {tooltip && (
                <Tooltip content={tooltip} preferredPosition="above">
                  <Box paddingInlineStart="100">
                    <Icon source={InfoIcon} tone="subdued" />
                  </Box>
                </Tooltip>
              )}
              {badge && (
                <Badge tone={colorTheme.badgeTone} size="small">
                  {badge}
                </Badge>
              )}
            </InlineStack>

            {IconComponent && (
              <Box>
                <Icon source={IconComponent} tone={colorTheme.valueTone} />
              </Box>
            )}
          </InlineStack>

          {/* Main value - flex grow to take available space */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Text
              variant="headingLg"
              tone={colorTheme.valueTone}
              fontWeight="semibold"
            >
              {(() => {
                try {
                  return formatter ? formatter(value) : formatValue(value);
                } catch (error) {
                  if (process.env.NODE_ENV === 'development') {
                    console.error('MetricCard: Error formatting value:', error, { value, formatter });
                  }
                  return '--';
                }
              })()}
            </Text>

            {subtitle && (
              <Text variant="bodySm" tone="subdued">
                {subtitle}
              </Text>
            )}
          </div>

          {/* Change indicator - fixed height at bottom */}
          <div style={{ height: '32px', display: 'flex', alignItems: 'center' }}>
            {renderChange()}
          </div>
      </Box>
    </Card>
  );
}

// Preset metric card variants for common use cases
export const RevenueCard = (props) => (
  <MetricCard
    color="success"
    tooltip={props.tooltip || "Total revenue generated from completed orders across all selected brands"}
    {...props}
  />
);

export const CommissionCard = (props) => (
  <MetricCard
    color="default"
    tooltip={props.tooltip || "Total commission earned based on configured rates and fulfilled order values +  Adjustments configured by the admin"}
    {...props}
  />
);

export const OrdersCard = (props) => (
  <MetricCard
    color="info"
    tooltip={props.tooltip || "Total number of orders fulfilled in the selected time period"}
    {...props}
  />
);

export const GrowthCard = (props) => (
  <MetricCard
    color={props.change?.direction === 'up' ? 'success' : 'critical'}
    tooltip="Growth compared to previous period"
    {...props}
  />
);